import { MakeLogicType, actions, connect, kea, key, listeners, path, props, reducers, selectors } from 'kea'

import {
    breakdownFilterToTaxonomicFilterType,
    filterToTaxonomicFilterType,
    propertyFilterTypeToPropertyDefinitionType,
} from 'lib/components/PropertyFilters/utils'
import { TaxonomicFilterGroupType } from 'lib/components/TaxonomicFilter/types'
import { keyForInsightLogicProps } from 'scenes/insights/sharedUtils'
import { teamLogic } from 'scenes/teamLogic'
import { userLogic } from 'scenes/userLogic'

import { cohortsModel } from '~/models/cohortsModel'
import { propertyDefinitionsModel } from '~/models/propertyDefinitionsModel'
import { AvailableFeature, InsightLogicProps } from '~/types'

import type { TaxonomicFilterValue } from '../../../../lib/components/TaxonomicFilter/types'
import type { Breakdown, BreakdownFilter } from '../../../../queries/schema/schema-general'
import type {
    CohortType,
    PropertyDefinition,
    PropertyDefinitionType,
    TeamPublicType,
    TeamType,
} from '../../../../types'
import { taxonomicBreakdownFilterLogic } from './taxonomicBreakdownFilterLogic'
import { isURLNormalizeable } from './taxonomicBreakdownFilterUtils'

export interface BreakdownTagLogicProps {
    insightProps: InsightLogicProps
    breakdown: string | number
    breakdownType: string
    isTrends: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface breakdownTagLogicValues {
    cohortsById: Partial<Record<number | string, CohortType>> // cohortsModel
    getPropertyDefinition: (
        s: TaxonomicFilterValue,
        type: PropertyDefinitionType,
        groupTypeIndex?: number
    ) => PropertyDefinition | null // propertyDefinitionsModel
    breakdownFilter: BreakdownFilter // taxonomicBreakdownFilterLogic
    globalBinCount: number | undefined // taxonomicBreakdownFilterLogic
    globalHistogramBinsUsed: boolean // taxonomicBreakdownFilterLogic
    globalNormalizeBreakdownUrl: boolean // taxonomicBreakdownFilterLogic
    globalPathCleaningEnabled: boolean // taxonomicBreakdownFilterLogic
    isMultipleBreakdownsEnabled: boolean // taxonomicBreakdownFilterLogic
    currentTeam: TeamPublicType | TeamType | null // teamLogic
    hasAvailableFeature: (feature: AvailableFeature, currentUsage?: number | undefined) => boolean // userLogic
    breakdown: number | string
    breakdownType: string
    hasAdvancedPaths: boolean
    hasPathCleaningFilters: boolean
    histogramBinCount: number | undefined
    histogramBinsUsed: boolean
    isHistogramable: boolean
    isNormalizeable: boolean
    isPathCleaningAvailable: boolean
    localHistogramBinCount: number | undefined
    localNormalizeBreakdownURL: boolean | undefined
    multipleBreakdown: Breakdown | undefined
    normalizeBreakdownURL: boolean
    pathCleaningEnabled: boolean
    propertyDefinition: PropertyDefinition | null
    taxonomicBreakdownType: TaxonomicFilterGroupType | undefined
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface breakdownTagLogicActions {
    removeBreakdownFromList: (
        breakdown: number | string,
        breakdownType: string
    ) => {
        breakdown: number | string
        breakdownType: string
    } // taxonomicBreakdownFilterLogic
    setHistogramBinCountInList: (
        breakdown: number | string,
        breakdownType: string,
        count: number | undefined
    ) => {
        breakdown: number | string
        breakdownType: string
        count: number | undefined
    } // taxonomicBreakdownFilterLogic
    setHistogramBinsUsedInList: (
        breakdown: number | string,
        breakdownType: string,
        binsUsed: boolean,
        binCount?: number | undefined
    ) => {
        binCount: number | undefined
        binsUsed: boolean
        breakdown: number | string
        breakdownType: string
    } // taxonomicBreakdownFilterLogic
    setNormalizeBreakdownURLInList: (
        breakdown: number | string,
        breakdownType: string,
        normalizeBreakdownURL: boolean
    ) => {
        breakdown: number | string
        breakdownType: string
        normalizeBreakdownURL: boolean
    } // taxonomicBreakdownFilterLogic
    setPathCleaningEnabledInList: (
        breakdown: number | string,
        breakdownType: string,
        pathCleaningEnabled: boolean
    ) => {
        breakdown: number | string
        breakdownType: string
        pathCleaningEnabled: boolean
    } // taxonomicBreakdownFilterLogic
    removeBreakdown: () => {
        value: true
    }
    setHistogramBinCount: (count: number) => {
        count: number
    }
    setHistogramBinsUsed: (binsUsed: boolean) => {
        binsUsed: boolean
    }
    setNormalizeBreakdownURL: (normalizeURL: boolean) => {
        normalizeURL: boolean
    }
    setPathCleaningEnabled: (pathCleaningEnabled: boolean) => {
        pathCleaningEnabled: boolean
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface breakdownTagLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        breakdown: (breakdown: number | string) => number | string
        breakdownType: (breakdownType: string) => string
        propertyDefinition: (
            getPropertyDefinition: (
                s: TaxonomicFilterValue,
                type: PropertyDefinitionType,
                groupTypeIndex?: number
            ) => PropertyDefinition | null,
            breakdown: number | string,
            breakdownType: string
        ) => PropertyDefinition | null
        isHistogramable: (isTrends: boolean, propertyDefinition: PropertyDefinition | null) => boolean
        isNormalizeable: (propertyDefinition: PropertyDefinition | null) => boolean
        multipleBreakdown: (
            breakdownFilter: BreakdownFilter,
            breakdown: number | string,
            breakdownType: string
        ) => Breakdown | undefined
        histogramBinsUsed: (
            isMultipleBreakdownsEnabled: boolean,
            multipleBreakdown: Breakdown | undefined,
            globalHistogramBinsUsed: boolean
        ) => boolean
        histogramBinCount: (
            isMultipleBreakdownsEnabled: boolean,
            localHistogramBinCount: number | undefined,
            globalBinCount: number | undefined,
            multipleBreakdown: Breakdown | undefined
        ) => number | undefined
        normalizeBreakdownURL: (
            isMultipleBreakdownsEnabled: boolean,
            localNormalizeBreakdownURL: boolean | undefined,
            globalNormalizeBreakdownUrl: boolean,
            multipleBreakdown: Breakdown | undefined
        ) => boolean
        pathCleaningEnabled: (globalPathCleaningEnabled: boolean) => boolean
        hasAdvancedPaths: (
            hasAvailableFeature: (feature: AvailableFeature, currentUsage?: number | undefined) => boolean
        ) => boolean
        hasPathCleaningFilters: (currentTeam: TeamPublicType | TeamType | null) => boolean
        isPathCleaningAvailable: (
            isNormalizeable: boolean,
            hasAdvancedPaths: boolean,
            hasPathCleaningFilters: boolean
        ) => boolean
        taxonomicBreakdownType: (
            isMultipleBreakdownsEnabled: boolean,
            breakdownFilter: BreakdownFilter,
            multipleBreakdown: Breakdown | undefined
        ) => TaxonomicFilterGroupType | undefined
    }
}

export type breakdownTagLogicType = MakeLogicType<
    breakdownTagLogicValues,
    breakdownTagLogicActions,
    BreakdownTagLogicProps,
    breakdownTagLogicMeta
>

export const breakdownTagLogic = kea<breakdownTagLogicType>([
    props({} as BreakdownTagLogicProps),
    key(({ insightProps, breakdown }) => `${keyForInsightLogicProps('new')(insightProps)}-${breakdown}`),
    path((key) => ['scenes', 'insights', 'BreakdownFilter', 'breakdownTagLogic', key]),
    connect(() => ({
        values: [
            propertyDefinitionsModel,
            ['getPropertyDefinition'],
            cohortsModel,
            ['cohortsById'],
            teamLogic,
            ['currentTeam'],
            userLogic,
            ['hasAvailableFeature'],
            taxonomicBreakdownFilterLogic,
            [
                'isMultipleBreakdownsEnabled',
                'histogramBinsUsed as globalHistogramBinsUsed',
                'histogramBinCount as globalBinCount',
                'normalizeBreakdownUrl as globalNormalizeBreakdownUrl',
                'pathCleaningEnabled as globalPathCleaningEnabled',
                'breakdownFilter',
            ],
        ],
        actions: [
            taxonomicBreakdownFilterLogic,
            [
                'removeBreakdown as removeBreakdownFromList',
                'setHistogramBinCount as setHistogramBinCountInList',
                'setNormalizeBreakdownURL as setNormalizeBreakdownURLInList',
                'setPathCleaningEnabled as setPathCleaningEnabledInList',
                'setHistogramBinsUsed as setHistogramBinsUsedInList',
            ],
        ],
    })),
    actions(() => ({
        removeBreakdown: true,
        setHistogramBinCount: (count: number) => ({
            count,
        }),
        setHistogramBinsUsed: (binsUsed: boolean) => ({
            binsUsed,
        }),
        setNormalizeBreakdownURL: (normalizeURL: boolean) => ({
            normalizeURL,
        }),
        setPathCleaningEnabled: (pathCleaningEnabled: boolean) => ({
            pathCleaningEnabled,
        }),
    })),
    reducers({
        localHistogramBinCount: [
            undefined as number | undefined,
            {
                setHistogramBinCount: (_, { count }) => count,
            },
        ],
        localNormalizeBreakdownURL: [
            undefined as boolean | undefined,
            {
                setNormalizeBreakdownURL: (_, { normalizeURL }) => normalizeURL,
            },
        ],
    }),
    selectors({
        breakdown: [(_, props) => [props.breakdown], (breakdown: number | string) => breakdown],
        breakdownType: [(_, props) => [props.breakdownType], (breakdownType: string) => breakdownType],
        propertyDefinition: [
            (s, p) => [s.getPropertyDefinition, p.breakdown, p.breakdownType],
            (
                getPropertyDefinition: (
                    s: import('lib/components/TaxonomicFilter/types').TaxonomicFilterValue,
                    type: import('~/types').PropertyDefinitionType,
                    groupTypeIndex?: number
                ) => null | import('~/types').PropertyDefinition,
                breakdown: number | string,
                breakdownType: string
            ) => getPropertyDefinition(breakdown, propertyFilterTypeToPropertyDefinitionType(breakdownType)),
        ],
        isHistogramable: [
            (s, p) => [p.isTrends, s.propertyDefinition],
            (isTrends: boolean, propertyDefinition: null | import('~/types').PropertyDefinition) =>
                isTrends && !!propertyDefinition?.is_numerical,
        ],
        isNormalizeable: [
            (s) => [s.propertyDefinition],
            (propertyDefinition: null | import('~/types').PropertyDefinition) =>
                isURLNormalizeable(propertyDefinition?.name || ''),
        ],
        multipleBreakdown: [
            (s) => [s.breakdownFilter, s.breakdown, s.breakdownType],
            ({ breakdowns }, breakdown: number | string, breakdownType: string) =>
                breakdowns?.find(
                    (savedBreakdown) => savedBreakdown.property === breakdown && savedBreakdown.type === breakdownType
                ),
        ],
        histogramBinsUsed: [
            (s) => [s.isMultipleBreakdownsEnabled, s.multipleBreakdown, s.globalHistogramBinsUsed],
            (
                isMultipleBreakdownsEnabled: boolean,
                multipleBreakdown: import('../../../../queries/schema').Breakdown | undefined,
                globalHistogramBinsUsed: boolean
            ) => {
                if (isMultipleBreakdownsEnabled) {
                    return multipleBreakdown?.histogram_bin_count != null
                }

                return globalHistogramBinsUsed
            },
        ],
        histogramBinCount: [
            (s) => [s.isMultipleBreakdownsEnabled, s.localHistogramBinCount, s.globalBinCount, s.multipleBreakdown],
            (
                isMultipleBreakdownsEnabled: boolean,
                localHistogramBinCount: number | undefined,
                globalBinCount: number | undefined,
                multipleBreakdown: import('../../../../queries/schema').Breakdown | undefined
            ) => {
                if (isMultipleBreakdownsEnabled) {
                    return localHistogramBinCount ?? multipleBreakdown?.histogram_bin_count ?? 10
                }

                return globalBinCount
            },
        ],
        normalizeBreakdownURL: [
            (s) => [
                s.isMultipleBreakdownsEnabled,
                s.localNormalizeBreakdownURL,
                s.globalNormalizeBreakdownUrl,
                s.multipleBreakdown,
            ],
            (
                isMultipleBreakdownsEnabled: boolean,
                localNormalizeBreakdownURL: boolean | undefined,
                globalNormalizeBreakdownUrl: boolean,
                multipleBreakdown: import('../../../../queries/schema').Breakdown | undefined
            ) => {
                if (isMultipleBreakdownsEnabled) {
                    return localNormalizeBreakdownURL ?? multipleBreakdown?.normalize_url ?? true
                }

                return globalNormalizeBreakdownUrl
            },
        ],
        pathCleaningEnabled: [
            (s) => [s.globalPathCleaningEnabled],
            (globalPathCleaningEnabled: boolean) => globalPathCleaningEnabled,
        ],
        hasAdvancedPaths: [
            (s) => [s.hasAvailableFeature],
            (hasAvailableFeature: (feature: AvailableFeature, currentUsage?: number | undefined) => boolean) =>
                hasAvailableFeature(AvailableFeature.PATHS_ADVANCED),
        ],
        hasPathCleaningFilters: [
            (s) => [s.currentTeam],
            (currentTeam: null | import('~/types').TeamPublicType | import('~/types').TeamType) =>
                (currentTeam?.path_cleaning_filters || []).length > 0,
        ],
        isPathCleaningAvailable: [
            (s) => [s.isNormalizeable, s.hasAdvancedPaths, s.hasPathCleaningFilters],
            (isNormalizeable: boolean, hasAdvancedPaths: boolean, hasPathCleaningFilters: boolean) =>
                isNormalizeable && hasAdvancedPaths && hasPathCleaningFilters,
        ],
        taxonomicBreakdownType: [
            (s) => [s.isMultipleBreakdownsEnabled, s.breakdownFilter, s.multipleBreakdown],
            (
                isMultipleBreakdownsEnabled: boolean,
                breakdownFilter: import('../../../../queries/schema').BreakdownFilter,
                multipleBreakdown: import('../../../../queries/schema').Breakdown | undefined
            ): TaxonomicFilterGroupType | undefined => {
                let breakdownType = isMultipleBreakdownsEnabled
                    ? filterToTaxonomicFilterType(
                          multipleBreakdown?.type,
                          multipleBreakdown?.group_type_index,
                          multipleBreakdown?.property
                      )
                    : breakdownFilterToTaxonomicFilterType(breakdownFilter)

                if (breakdownType === TaxonomicFilterGroupType.Cohorts) {
                    breakdownType = TaxonomicFilterGroupType.CohortsWithAllUsers
                }

                return breakdownType
            },
        ],
    }),
    listeners(({ values, actions }) => ({
        removeBreakdown: () => {
            actions.removeBreakdownFromList(values.breakdown, values.breakdownType)
        },
        setHistogramBinCount: ({ count }) => {
            actions.setHistogramBinCountInList(values.breakdown, values.breakdownType, count)
        },
        setNormalizeBreakdownURL: ({ normalizeURL }) => {
            actions.setNormalizeBreakdownURLInList(values.breakdown, values.breakdownType, normalizeURL)
        },
        setPathCleaningEnabled: ({ pathCleaningEnabled }) => {
            actions.setPathCleaningEnabledInList(values.breakdown, values.breakdownType, pathCleaningEnabled)
        },
        setHistogramBinsUsed: ({ binsUsed }) => {
            actions.setHistogramBinsUsedInList(
                values.breakdown,
                values.breakdownType,
                binsUsed,
                values.histogramBinCount
            )
        },
    })),
])
